import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Utensils, PartyPopper, ArrowRight, CheckCircle, Heart, Sparkles, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Programs = () => {
  const { language } = useLanguage();

  const programs = [
    {
      icon: <BookOpen className="w-12 h-12" />,
      title: language === 'en' ? 'Education & Tutoring Program' : 'Programa de Educación y Tutoría',
      tagline: language === 'en' 
        ? 'Education builds confidence. Confidence builds opportunity.'
        : 'La educación construye confianza. La confianza construye oportunidad.',
      description: language === 'en'
        ? 'We provide comprehensive educational support to ensure every child has the tools they need to succeed academically and build a brighter future.'
        : 'Proporcionamos apoyo educativo integral para asegurar que cada niño tenga las herramientas necesarias para tener éxito académico y construir un futuro mejor.',
      features: language === 'en' ? [
        'Daily homework help and tutoring',
        'School supplies and textbooks',
        'Literacy development programs',
        'Scholarships and advancement support',
        'Computer access and digital literacy',
        'Academic mentorship and guidance'
      ] : [
        'Ayuda diaria con tareas y tutoría',
        'Útiles escolares y libros de texto',
        'Programas de desarrollo de alfabetización',
        'Becas y apoyo para avanzar',
        'Acceso a computadoras y alfabetización digital',
        'Mentoría y orientación académica'
      ],
      images: [
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/70af011f-e935-4f26-0c50-8ed9bd6a1e00/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/bb7de01a-42b8-4b8f-8642-d92b12108a00/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/c25a48c2-dd75-47c5-9e66-f3598d8d6200/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/065fc29a-111e-4913-446e-2532cad89600/public'
      ],
      gradient: 'from-sh-primary to-sh-bright',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50'
    },
    {
      icon: <Utensils className="w-12 h-12" />,
      title: language === 'en' ? 'Nutrition Program' : 'Programa de Nutrición',
      tagline: language === 'en'
        ? 'A nourished child can learn, grow, and dream.'
        : 'Un niño bien alimentado puede aprender, crecer y soñar.',
      description: language === 'en'
        ? 'Proper nutrition is fundamental to child development. We ensure every child receives the healthy meals and clean water they need to thrive.'
        : 'La nutrición adecuada es fundamental para el desarrollo infantil. Aseguramos que cada niño reciba las comidas saludables y el agua limpia que necesitan para prosperar.',
      features: language === 'en' ? [
        'Daily nutritious meals',
        'Healthy snacks and food access',
        'Clean drinking water',
        'Emergency food support',
        'Nutrition education',
        'Special dietary accommodations'
      ] : [
        'Comidas nutritivas diarias',
        'Snacks saludables y acceso a alimentos',
        'Agua potable limpia',
        'Apoyo alimentario de emergencia',
        'Educación nutricional',
        'Adaptaciones dietéticas especiales'
      ],
      images: [
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/72a33fb1-0003-4765-3e11-970650d54200/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/4b209da4-0224-4849-baf8-de66f8627100/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/d319ae70-49ed-49e1-7e2d-ede7c8544200/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/63eee5bb-7977-47da-f06f-aa3bbc4e7d00/public'
      ],
      gradient: 'from-orange-500 to-amber-500',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50'
    },
    {
      icon: <PartyPopper className="w-12 h-12" />,
      title: language === 'en' ? 'Celebration Program' : 'Programa de Celebración',
      tagline: language === 'en'
        ? 'Trauma is not erased by survival. It is healed through joy.'
        : 'El trauma no se borra con la supervivencia. Se sana a través de la alegría.',
      description: language === 'en'
        ? "Every child deserves to celebrate life's special moments. Our celebration program ensures that birthdays, holidays, and milestones are honored and remembered."
        : 'Cada niño merece celebrar los momentos especiales de la vida. Nuestro programa de celebración asegura que los cumpleaños, fiestas y logros sean honrados y recordados.',
      features: language === 'en' ? [
        'Birthday celebrations for every child',
        'Christmas events and gift giving',
        'Holiday gatherings and traditions',
        'Special outings and field trips',
        'Milestone recognition',
        'Talent shows and performances'
      ] : [
        'Celebraciones de cumpleaños para cada niño',
        'Eventos navideños y entrega de regalos',
        'Reuniones festivas y tradiciones',
        'Salidas especiales y excursiones',
        'Reconocimiento de logros',
        'Shows de talentos y presentaciones'
      ],
      images: [
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/6da013ac-c35c-4a00-9d6e-93cc3fbfae00/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/4d8c32f4-4484-4817-9586-892f91d47800/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/78add044-893e-4746-1daf-3b4a6e343800/public',
        'https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/ca7da476-5bd4-4da3-8eeb-903e0052df00/public'
      ],
      gradient: 'from-pink-500 to-rose-500',
      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="programs-page">
      {/* Hero Section - WOW Effect */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/9ecc4903-3000-43ad-43b6-b9bbdc1e5300/public"
            alt="Children at The Safe House"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sh-dark/95 via-sh-dark/85 to-sh-dark/70"></div>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.2 }}
            className="absolute top-20 right-20 w-72 h-72 bg-colombia-yellow rounded-full filter blur-[100px]"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.15, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.4 }}
            className="absolute bottom-20 left-20 w-96 h-96 bg-sh-primary rounded-full filter blur-[120px]"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.6 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-400 rounded-full filter blur-[100px]"
          />
        </div>
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-colombia-yellow/20 backdrop-blur-sm border border-colombia-yellow/40 rounded-full mb-6"
              >
                <Sparkles className="w-5 h-5 text-colombia-yellow" />
                <span className="text-colombia-yellow text-sm font-bold tracking-wider uppercase">
                  {language === 'en' ? 'Our Programs' : 'Nuestros Programas'}
                </span>
              </motion.div>
              
              {/* Main Heading */}
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight"
              >
                {language === 'en' ? 'Restoring Childhood.' : 'Restaurando la Infancia.'}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-colombia-yellow via-amber-400 to-colombia-yellow">
                  {language === 'en' ? 'Building Strong Foundations' : 'Construyendo Bases Sólidas'}
                </span>
                <br />
                <span className="text-white/90">
                  {language === 'en' ? 'for the Future.' : 'para el Futuro.'}
                </span>
              </motion.h1>
              
              {/* Description */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg md:text-xl text-gray-200 max-w-3xl mb-6 leading-relaxed"
              >
                {language === 'en'
                  ? "Through our programs, children receive nourishing meals that strengthen their bodies, educational support that helps them succeed in school, mentorship that builds confidence, and Christian guidance that shapes their character and faith."
                  : "A través de nuestros programas, los niños reciben comidas nutritivas que fortalecen sus cuerpos, apoyo educativo que les ayuda a tener éxito en la escuela, mentoría que construye confianza y guía cristiana que moldea su carácter y fe."}
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-base md:text-lg text-colombia-yellow font-medium italic"
              >
                {language === 'en'
                  ? "Each program creates a safe space where children can learn, grow, develop their talents, and step into a brighter future."
                  : "Cada programa crea un espacio seguro donde los niños pueden aprender, crecer, desarrollar sus talentos y dar un paso hacia un futuro más brillante."}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Sections */}
      <section className="py-16 md:py-24">
        {programs.map((program, index) => (
          <div 
            key={index} 
            className={`${program.bgColor} py-16 md:py-20 ${index > 0 ? 'mt-4' : ''}`}
          >
            <div className="container mx-auto px-4">
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-center`}>
                
                {/* Images Gallery - Enhanced for Mobile */}
                <motion.div 
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="w-full lg:w-1/2"
                >
                  {/* Mobile: Horizontal scrolling gallery */}
                  <div className="lg:hidden">
                    <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
                      {program.images.map((img, imgIndex) => (
                        <div 
                          key={imgIndex} 
                          className="flex-shrink-0 w-72 snap-center first:ml-0 last:mr-0"
                        >
                          <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                            <img 
                              src={img} 
                              alt={`${program.title} ${imgIndex + 1}`} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-2">
                      {language === 'en' ? '← Swipe to see more →' : '← Desliza para ver más →'}
                    </p>
                  </div>
                  
                  {/* Desktop: Grid layout */}
                  <div className="hidden lg:block">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative rounded-2xl overflow-hidden shadow-xl col-span-2">
                        <img 
                          src={program.images[0]} 
                          alt={`${program.title} 1`} 
                          className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      {program.images.slice(1).map((img, imgIndex) => (
                        <div key={imgIndex} className="relative rounded-2xl overflow-hidden shadow-xl">
                          <img 
                            src={img} 
                            alt={`${program.title} ${imgIndex + 2}`} 
                            className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div 
                  initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="w-full lg:w-1/2"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${program.gradient} flex items-center justify-center text-white mb-6 shadow-lg`}>
                    {program.icon}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {program.title}
                  </h2>
                  <p className="text-xl text-sh-primary font-semibold mb-4 italic">
                    "{program.tagline}"
                  </p>
                  <p className="text-gray-600 text-lg mb-8">
                    {program.description}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-sh-primary to-sh-bright relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full filter blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-colombia-yellow/20 rounded-full filter blur-2xl"></div>
        </div>
        
        <div className="relative container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              {language === 'en' ? 'Support Our Programs' : 'Apoya Nuestros Programas'}
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
              {language === 'en'
                ? 'Your donation directly funds these life-changing programs. Every dollar makes a difference in a child\'s life.'
                : 'Tu donación financia directamente estos programas que cambian vidas. Cada dólar hace la diferencia en la vida de un niño.'}
            </p>
            <Link
              to="/donate"
              className="inline-flex items-center gap-2 px-10 py-5 bg-white text-sh-primary rounded-full font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105"
              data-testid="programs-donate-button"
            >
              {language === 'en' ? 'Donate Now' : 'Donar Ahora'}
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
